import React, { useMemo, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { useStripeAdminAccounts } from '../hooks/useStripeAdminAccounts';
import { StripeConnectDashboard } from '../components/StripeConnectDashboard';

/**
 * Page « Administration Stripe » (lecture seule) pour les vendeurs.
 *
 * Visible uniquement si l'utilisateur possède ≥1 compte Stripe supervisable
 * (résolu serveur : sociétés en portée + ownsStripeAccount). Un sélecteur
 * s'affiche quand plusieurs comptes sont disponibles (tête décentralisée qui
 * supervise ses agences).
 */
export const StripeAdminPage: React.FC = () => {
  const { data: accounts, isLoading, isError } = useStripeAdminAccounts();
  const [selectedSocietyId, setSelectedSocietyId] = useState<string | null>(null);

  const effectiveSocietyId = useMemo(() => {
    if (selectedSocietyId) return selectedSocietyId;
    return accounts && accounts.length > 0 ? accounts[0].societyId : null;
  }, [selectedSocietyId, accounts]);

  if (isLoading) {
    return <div className="tw-p-6 tw-text-gray-500">Chargement du dashboard Stripe…</div>;
  }

  if (isError) {
    return (
      <div className="tw-p-6 tw-text-red-600">
        Impossible de charger vos comptes Stripe. Réessayez plus tard.
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <div className="tw-p-6 tw-text-gray-500">
        Aucun compte Stripe à afficher pour votre compte.
      </div>
    );
  }

  return (
    <div className="tw-p-4 md:tw-p-6">
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3 tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <CreditCard className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
          <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">
            Mes paiements en ligne
          </h1>
        </div>

        {accounts.length > 1 && (
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <span className="tw-text-gray-500">Société&nbsp;:</span>
            <select
              className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-1.5"
              value={effectiveSocietyId ?? ''}
              onChange={(e) => setSelectedSocietyId(e.target.value)}
            >
              {accounts.map((acc) => (
                <option key={acc.societyId} value={acc.societyId}>
                  {acc.societyName}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {effectiveSocietyId && (
        <StripeConnectDashboard key={effectiveSocietyId} societyId={effectiveSocietyId} />
      )}
    </div>
  );
};

export default StripeAdminPage;
